/**
 * The public page address. A sub-surface with its own asynchronous contract, and its own rules.
 *
 * EDITABLE UNTIL APPROVAL, FROZEN AFTER. That is ruled (F3) and enforced by a database trigger;
 * the API answers 409 SLUG_FROZEN afterwards. So once `slugFrozen` is true and an address is on
 * file there is no field at all: the address is drawn as a link to the live page.
 *
 * SAY ONCE, BEFORE APPROVAL, THAT THIS IS PERMANENT. Saying it after approval is too late; saying
 * it on every keystroke is nagging.
 *
 * ★ AND AN APPROVED AUTHOR WITH NO ADDRESS YET MAY STILL CHOOSE ONE, ONCE (design 14, finding F3 -
 * not the corpus's F3 above). The freeze needs an address to freeze, so an author the import
 * brought over from the old apps arrives approved with this field empty and editable, and the
 * first address they save is the last. The field said "once your application is approved this
 * cannot be changed" to somebody already approved; it now takes the page's one answer,
 * `AddressRule` (domain/states.ts), and says which of the three is true.
 *
 * A FREE ANSWER RESERVES NOTHING. The address is claimed when the profile saves, so "available"
 * here is a courtesy and the save can still fail. The wording never implies a hold.
 *
 * THE SUGGESTION IS NOT A CONVENIENCE. `checkSlugAvailability` with NO slug returns a candidate
 * transliterated from the display name, and for an author writing in a non-Latin script it is the
 * only path to a valid address at all — the ruled shape is lowercase Latin letters, digits and
 * single hyphens.
 */
import { Anchor, Button, Group, Loader, Stack, Text, TextInput } from '@mantine/core';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from '../../../i18n';
import { WEBSITE_ORIGIN } from '../../auth/handoff/website-handoff';
import { tooManyRequests } from '../api/problem';
import { PUBLIC_PAGE_PREFIX } from '../domain/limits';
import type { AddressRule } from '../domain/states';
import type { MissingSuggestion, SlugCheck } from '../data/useSlugCheck';

export interface SlugFieldProps {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** What may still happen to the address - `addressRule` in domain/states.ts. */
  readonly rule: AddressRule;
  readonly error: string | undefined;
  readonly check: SlugCheck;
  readonly disabled?: boolean;
}

/** What the field says under its label, per rule. */
const RULE_DESCRIPTION = {
  fixed: 'slug.rule.fixed',
  choose_once: 'slug.rule.chooseOnce',
  until_approval: 'slug.rule.untilApproval',
} as const satisfies Readonly<Record<AddressRule, string>>;

/** Why "Suggest one from my name" came back empty, in the author's terms (design 14, F7). */
const MISSING_SUGGESTION = {
  name_not_saved: 'slug.missingSuggestion.nameNotSaved',
  name_gave_none: 'slug.missingSuggestion.nameGaveNone',
} as const satisfies Readonly<Record<MissingSuggestion, string>>;

export function SlugField({
  id,
  value,
  onChange,
  rule,
  error,
  check,
  disabled,
}: SlugFieldProps): ReactNode {
  const { t } = useTranslation('application');
  const frozen = rule === 'fixed';
  const preview = value.trim().length > 0 ? `${PUBLIC_PAGE_PREFIX}${value.trim()}` : null;

  // ★ AN ADDRESS THAT IS ALREADY FIXED IS A LINK, NOT A FIELD (the owner, 2026-10-19): there is
  // nothing left to type, so the author sees where readers find them and can open it - the live
  // page on the website, in a new tab, as "Open your public page" beside the form does.
  if (frozen && preview !== null) {
    return (
      <Stack gap={4} id={id}>
        <Text size="sm" fw={500}>
          {t('slug.label')}
        </Text>
        <Anchor
          href={`${WEBSITE_ORIGIN}/authors/${encodeURIComponent(value.trim())}`}
          target="_blank"
          rel="noopener"
          size="sm"
        >
          {preview}
        </Anchor>
      </Stack>
    );
  }

  const status = ((): ReactNode => {
    if (frozen) return null;
    if (error !== undefined) return null;
    switch (check.phase) {
      case 'checking':
        return (
          <Group gap={6} aria-live="polite">
            <Loader size={12} />
            <Text size="xs" c="dimmed">
              {t('slug.checking')}
            </Text>
          </Group>
        );
      case 'answered': {
        const result = check.result;
        if (result === undefined) return null;
        return result.available ? (
          <Text size="xs" aria-live="polite" style={{ color: 'oklch(var(--mx-success))' }}>
            {t('slug.free')}
          </Text>
        ) : (
          <Text size="xs" aria-live="polite" style={{ color: 'oklch(var(--mx-danger))' }}>
            {result.reason ?? t('slug.notAvailable')}
            {result.suggestion !== null && result.suggestion !== undefined
              ? ` ${t('slug.trySuggestion', { suggestion: result.suggestion })}`
              : ''}
          </Text>
        );
      }
      case 'failed':
        return (
          <Text size="xs" c="dimmed" aria-live="polite">
            {check.rateLimited
              ? tooManyRequests(check.retryAfterSeconds, t('slug.tooManyChecks'))
              : t('slug.checkFailed')}
          </Text>
        );
      case 'too_short':
        return (
          <Text size="xs" c="dimmed">
            {t('slug.tooShort')}
          </Text>
        );
      case 'bad_shape':
        return (
          <Text size="xs" c="dimmed">
            {t('slug.badShape')}
          </Text>
        );
      default:
        return null;
    }
  })();

  return (
    <Stack gap={6} id={id}>
      <TextInput
        label={t('slug.label')}
        description={t(RULE_DESCRIPTION[rule])}
        value={value}
        disabled={disabled === true || frozen}
        readOnly={frozen}
        error={error}
        leftSectionWidth={0}
        placeholder={t('slug.placeholder')}
        onChange={(event) => {
          onChange(event.currentTarget.value);
        }}
      />

      {preview !== null ? (
        <Text size="xs" c="dimmed">
          <Trans
            t={t}
            i18nKey="slug.preview"
            values={{ address: preview }}
            components={{ strong: <strong /> }}
          />
        </Text>
      ) : null}

      {status}

      {!frozen && value.trim().length === 0 ? (
        <Stack gap={4}>
          <Group gap="xs" align="center">
            <Button
              size="compact-xs"
              variant="subtle"
              loading={check.suggestionLoading}
              onClick={check.requestSuggestion}
            >
              {t('slug.suggest')}
            </Button>
            {check.suggestion !== undefined ? (
              <Anchor
                component="button"
                type="button"
                size="xs"
                onClick={() => {
                  onChange(check.suggestion ?? '');
                }}
              >
                {t('slug.useSuggestion', { suggestion: check.suggestion })}
              </Anchor>
            ) : null}
          </Group>
          {check.missingSuggestion !== null ? (
            <Text size="xs" c="dimmed" aria-live="polite">
              {t(MISSING_SUGGESTION[check.missingSuggestion])}
            </Text>
          ) : null}
        </Stack>
      ) : null}

      {!frozen ? (
        <Text size="xs" c="dimmed">
          {t('slug.numberedPage')}
        </Text>
      ) : null}
    </Stack>
  );
}
